import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmDialog from './ConfirmDialog';
import { useFullscreen } from '../contexts/FullscreenContext';
import { useChat } from '../contexts/ChatContext';
import { chatErrorMessage } from '../services/chatErrorMessage';
import ChatMessage from './chat/ChatMessage';
import ChatProgress from './chat/ChatProgress';
import uiActionHandler from '../services/uiActionHandler';
import { useEntitlements } from '../books/entitlements';
import BathroomConstraintsModal from './BathroomConstraintsPanel';
import { inferRoomType } from '../utils/roomTypeUtils';
import './LogoPopup.css';

// The mic sends what the box holds after this long with nothing more heard,
// as the iOS app stops listening (SpeechRecognitionService.silenceTimeout).
const SILENCE_MS = 2000;

/**
 * Lay actions out in rows. Consecutive actions sharing the same `group` string
 * stay together and never share a row with a neighbouring group, so a caller
 * can keep e.g. the two checkpoint buttons on their own line no matter how
 * many items precede them. Ungrouped actions behave as before (a single run
 * chunked two-per-row). A run's first action may set `perRow` to widen its
 * rows (the three exports ride one row).
 */
// Running an action normally dismisses the menu — you asked for a thing, the
// thing happens, the menu is out of the way. An action that changes what the
// MENU ITSELF shows is the exception: it sets `keepOpen`, so revealing the
// admin buttons leaves them on screen instead of making you reopen the menu
// to see what changed.
const runAction = (action, close) => {
  if (action.disabled) return;
  action.onClick();
  if (!action.keepOpen) close();
};

const chunkActionRows = (actions) => {
  const runs = [];
  actions.forEach((action) => {
    const group = action.group ?? null;
    const last = runs[runs.length - 1];
    if (last && last.group === group) last.actions.push(action);
    else runs.push({ group, actions: [action] });
  });

  return runs.flatMap((run) => {
    const perRow = run.actions[0]?.perRow || 2;
    const rows = [];
    for (let i = 0; i < run.actions.length; i += perRow) {
      rows.push(run.actions.slice(i, i + perRow));
    }
    return rows;
  });
};

const LogoPopup = ({
  onLogout,
  customActions = [],
  onExportPDF,
  onExportIFC,
  onExportDXF,
  exportLoading = false,
  roomData = null,
  structureData = null,
  disabledConstraints = [],
  onDisabledConstraintsChange,
  // Admin tooling visible (isAdmin && the "A" toggle) — gates the
  // Bathroom Constraints reference, which is a dev aid, not a user tool.
  showAdminTools = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Note: isClosing state and animation removed - was causing UI freezes
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { mayUseAssistant } = useEntitlements();

  // Get chat state from context
  const {
    messages,
    isLoading,
    currentTaskId,
    progress,
    inputValue,
    isChatActive,
    conversationContext,
    setMessages,
    setIsLoading,
    setCurrentTaskId,
    setProgress,
    setInputValue,
    setIsChatActive,
    addMessage,
    clearMessages,
    updateContext,
    trackNavigation,
    sendBrowserTurn,
    stopTurn,
    turnRunning,
  } = useChat();

  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const navigate = useNavigate();
  const location = useLocation();
  const popupRef = useRef(null);
  const logoRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  // The mic sends on its own after a silence (voice plan, decision 3): the
  // timer and what it sends, read when it fires, not when it was set.
  const silenceTimer = useRef(null);
  const sendAfterSilence = useRef(null);
  const inputValueRef = useRef('');
  inputValueRef.current = inputValue;
  useEffect(() => () => clearTimeout(silenceTimer.current), []);

  const isProjectPage = location.pathname.startsWith('/project');

  // Set up UI action handlers
  useEffect(() => {
    // Set up navigation handler
    uiActionHandler.setNavigationCallback((path, options) => {
      if (typeof path === 'number') {
        // Handle back/forward navigation
        navigate(path);
      } else {
        navigate(path);
        // Track navigation for context
        trackNavigation(path);
      }
      // Close the popup after navigation
      handleClose();
    });

    // Set up scene action handler
    uiActionHandler.setSceneCallback((actionType, subAction, data) => {
      console.log('LogoPopup scene callback:', { actionType, subAction, data });
      // Dispatch custom event for scene components to handle
      const event = new CustomEvent('sceneAction', {
        detail: { actionType, subAction, data },
      });
      window.dispatchEvent(event);
    });

    // Set up action event listeners
    const handleTriggerExport = (e) => {
      const format = e.detail?.format;
      if (format === 'pdf' && onExportPDF) {
        onExportPDF();
        handleClose();
      } else if (format === 'ifc' && onExportIFC) {
        onExportIFC();
        handleClose();
      } else if (format === 'dxf' && onExportDXF) {
        onExportDXF();
        handleClose();
      }
    };

    const handleToggleFullscreen = () => {
      toggleFullscreen();
      handleClose();
    };

    const handleClearChatEvent = () => {
      clearMessages();
    };

    const handleTriggerLogout = () => {
      handleLogout();
    };

    const handleTriggerCustomAction = (e) => {
      const { label } = e.detail;
      const action = customActions.find((a) => a.label === label);
      if (action) runAction(action, handleClose);
    };

    window.addEventListener('triggerExport', handleTriggerExport);
    window.addEventListener('toggleFullscreen', handleToggleFullscreen);
    window.addEventListener('clearChat', handleClearChatEvent);
    window.addEventListener('triggerLogout', handleTriggerLogout);
    window.addEventListener('triggerCustomAction', handleTriggerCustomAction);

    return () => {
      // Clean up handlers on unmount
      uiActionHandler.setNavigationCallback(null);
      uiActionHandler.setSceneCallback(null);
      window.removeEventListener('triggerExport', handleTriggerExport);
      window.removeEventListener('toggleFullscreen', handleToggleFullscreen);
      window.removeEventListener('clearChat', handleClearChatEvent);
      window.removeEventListener('triggerLogout', handleTriggerLogout);
      window.removeEventListener(
        'triggerCustomAction',
        handleTriggerCustomAction
      );
    };
  }, [
    navigate,
    trackNavigation,
    onExportPDF,
    onExportIFC,
    onExportDXF,
    toggleFullscreen,
    clearMessages,
    customActions,
  ]);

  // Handle Enter key to open chat
  useEffect(() => {
    if (!mayUseAssistant) return undefined;
    const handleKeyPress = (e) => {
      // Only open if Enter is pressed and chat is not already open
      // Don't trigger if user is typing in an input field or textarea
      if (e.key === 'Enter' && !isOpen) {
        const activeElement = document.activeElement;
        // Enter must keep its native meaning on anything focusable: typing
        // in a field, activating a focused button/link/select (e.g. the
        // "Delete" in a confirm dialog, "Log In"), or anything inside an
        // open dialog. Only a bare page (body / non-interactive focus)
        // opens the chat.
        const tag = activeElement?.tagName;
        const isInteractive =
          activeElement &&
          (tag === 'INPUT' ||
            tag === 'TEXTAREA' ||
            tag === 'SELECT' ||
            tag === 'BUTTON' ||
            tag === 'A' ||
            activeElement.isContentEditable ||
            activeElement.getAttribute('role') === 'button' ||
            activeElement.closest('[role="dialog"]'));

        if (!isInteractive) {
          e.preventDefault();
          openChat();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, mayUseAssistant]);

  // A failed turn shows in the conversation, not only in the console.
  const reportChatError = (error) => {
    addMessage({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      role: 'assistant',
      content: chatErrorMessage(error),
      timestamp: new Date(),
    });
  };

  const openChat = () => {
    if (!isOpen) {
      setIsOpen(true);
      // Focus the text area after opening
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 50);
    }
  };

  const handleLogoClick = () => {
    if (isOpen) {
      handleClose();
    } else {
      openChat();
    }
  };

  const handleClose = () => {
    // Close immediately - animation was causing UI freezes
    if (!isOpen) return;
    setIsOpen(false);
  };

  const handleBackToProjects = () => {
    navigate('/');
    handleClose();
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    onLogout();
    handleClose();
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleFullscreen = () => {
    toggleFullscreen();
    handleClose();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startSpeechRecognition = () => {
    if (
      !('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    ) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome or Edge.'
      );
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setInputValue((prev) => prev + finalTranscript);
        }
        // Anything heard, final or not, means the user is still speaking.
        clearTimeout(silenceTimer.current);
        silenceTimer.current = setTimeout(
          () => sendAfterSilence.current?.(),
          SILENCE_MS
        );
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        clearTimeout(silenceTimer.current);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    if (isListening) {
      clearTimeout(silenceTimer.current);
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  /** Send `text` as the user's message: the Send button, Enter, or the mic after a silence. */
  const sendText = async (text) => {
    clearTimeout(silenceTimer.current);
    if (!text.trim() || isLoading) return;

    // Stop listening if active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    const userMessage = text.trim();
    setInputValue('');

    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Activate chat mode
    if (!isChatActive) {
      setIsChatActive(true);
    }

    // The new chat, the only one the web offers (Step 4 of
    // claude-docs/2026-10-31-chat-app-actions-plan.md): the model runs in the
    // browser with the app's own actions, and does what the old chat's quick
    // commands did with `show`.
    if (!mayUseAssistant) return;
    await sendBrowserTurn(userMessage);
  };
  const handleSendMessage = () => sendText(inputValue);
  // What the box holds when the silence ends: typed words and heard ones.
  sendAfterSilence.current = () => sendText(inputValueRef.current);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Add a small delay to ensure DOM is rendered. `progress` too: a turn the
    // browser runs reports its steps through it as they happen.
    if (isOpen && isChatActive) {
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    }
  }, [messages, progress, isOpen, isChatActive]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        logoRef.current &&
        !logoRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    // Use mousedown with capture phase to catch events before they reach the canvas
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Auto-resize textarea when value changes programmatically (e.g., from speech recognition)
  // or when the popup opens
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (inputValue) {
        textareaRef.current.style.height =
          Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      }
      // Auto-focus textarea when popup opens or chat mode changes
      if (isOpen) {
        textareaRef.current.focus();
      }
    }
  }, [inputValue, isOpen, isChatActive]);

  return (
    <>
      <img
        ref={logoRef}
        src="/AppLogo.png"
        alt="Housfully"
        className="header-logo clickable-logo"
        onClick={handleLogoClick}
        style={{ position: 'relative', zIndex: 1003 }} // Keep logo above popup content
      />

      {isOpen && (
        <>
          {/* Invisible backdrop to catch clicks */}
          {/* Backdrop - click handling is done via document listener */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1001,
              background: 'transparent',
            }}
          />
          <div className="logo-popup-overlay">
            <div
              ref={popupRef}
              className="logo-popup-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="logo-popup-body">
                {/* Housfully text next to logo */}
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '80px' /* Position to the right of logo with more gap (16px padding + 48px logo + 16px gap) */,
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    color: '#54524F',
                    display: 'flex',
                    alignItems: 'center',
                    height: '48px',
                  }}
                >
                  Housfully
                </div>
                {/* The assistant: contractors and admins only (mayUseAssistant). */}
                {mayUseAssistant &&
                  (!isChatActive ? (
                    // Initial input view
                    <div
                      className="input-container"
                      style={{ marginTop: '0', marginLeft: '0' }}
                    >
                      <textarea
                        ref={textareaRef}
                        className="logo-popup-input"
                        placeholder="Type your request here."
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      />
                      {/* Microphone icon - top left */}
                      <svg
                        className={`microphone-icon ${isListening ? 'recording' : ''}`}
                        onClick={startSpeechRecognition}
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '12px',
                          cursor: 'pointer',
                          opacity: isListening ? 1 : 0.6,
                          color: isListening ? '#FF0000' : '#54524F',
                        }}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        title={
                          isListening ? 'Stop recording' : 'Start voice input'
                        }
                      >
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                      <div className="quick-command-hint">
                        Ask in your own words, like "paint the kitchen walls
                        navy"
                      </div>
                      {/* Send icon - bottom right */}
                      <svg
                        className="send-icon"
                        onClick={handleSendMessage}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          bottom: '12px',
                          cursor:
                            inputValue.trim() && !isLoading
                              ? 'pointer'
                              : 'not-allowed',
                          opacity: inputValue.trim() && !isLoading ? 1 : 0.3,
                          color: '#007AFF',
                        }}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        title="Send message"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </div>
                  ) : (
                    // Chat view
                    <div className="logo-popup-chat-container">
                      <div className="logo-popup-messages">
                        {messages.map((message) => (
                          <ChatMessage key={message.id} message={message} />
                        ))}
                        {progress && <ChatProgress progress={progress} />}
                        {turnRunning && (
                          <button
                            type="button"
                            className="chat-stop-button"
                            onClick={stopTurn}
                          >
                            Stop
                          </button>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      <div className="logo-popup-input-wrapper">
                        <textarea
                          ref={textareaRef}
                          className="logo-popup-chat-input"
                          placeholder="Type your message..."
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={handleInputKeyDown}
                          disabled={isLoading}
                          rows="1"
                          style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        />
                        {/* Microphone icon for chat mode */}
                        <svg
                          className={`microphone-icon ${isListening ? 'recording' : ''}`}
                          onClick={startSpeechRecognition}
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '10px',
                            cursor: 'pointer',
                            opacity: isListening ? 1 : 0.6,
                            color: isListening ? '#FF0000' : '#54524F',
                          }}
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          title={
                            isListening ? 'Stop recording' : 'Start voice input'
                          }
                        >
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                        <svg
                          className="send-icon"
                          onClick={handleSendMessage}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            bottom: '16px',
                            cursor:
                              inputValue.trim() && !isLoading
                                ? 'pointer'
                                : 'not-allowed',
                            opacity: inputValue.trim() && !isLoading ? 1 : 0.3,
                            color: '#007AFF',
                          }}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                      </div>
                    </div>
                  ))}

                {/* Chat action buttons - shown when chat is active */}
                {mayUseAssistant && isChatActive && (
                  <div className="action-buttons-container">
                    <div className="action-buttons">
                      <button className="action-button" onClick={clearMessages}>
                        Delete Chat
                      </button>
                      <button
                        className="action-button"
                        onClick={() => {
                          handleClose();
                          // Keep messages and chat state for when user reopens
                        }}
                      >
                        Minimize Chat
                      </button>
                    </div>
                  </div>
                )}

                {!isChatActive && (
                  <div className="action-buttons-container">
                    {/* Render non-admin actions first, in rows of 2 */}
                    {(() => {
                      const regularActions = customActions.filter(
                        (action) => !action.isAdmin
                      );
                      const adminActions = customActions.filter(
                        (action) => action.isAdmin
                      );

                      return (
                        <>
                          {/* Regular actions */}
                          {regularActions.length > 0 && (
                            <>
                              {chunkActionRows(regularActions).map(
                                (rowActions, rowIndex) => (
                                  <div
                                    key={`regular-${rowIndex}`}
                                    className="action-buttons"
                                  >
                                    {rowActions.map((action) => (
                                      <button
                                        key={action.label}
                                        className="action-button"
                                        onClick={() =>
                                          runAction(action, handleClose)
                                        }
                                        disabled={action.disabled}
                                        title={action.title}
                                      >
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                )
                              )}
                            </>
                          )}

                          {/* Back to Projects button for project page without custom actions */}
                          {!regularActions.length &&
                            !adminActions.length &&
                            isProjectPage && (
                              <div className="action-buttons">
                                <button
                                  className="action-button"
                                  onClick={handleBackToProjects}
                                >
                                  Back to Projects
                                </button>
                              </div>
                            )}

                          {/* Export buttons - render after regular actions */}
                          {(onExportPDF || onExportIFC || onExportDXF) && (
                            <div
                              className="action-buttons"
                              style={{ display: 'flex', gap: '8px' }}
                            >
                              {onExportPDF && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportPDF();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  {exportLoading
                                    ? 'Exporting...'
                                    : 'Export PDF'}
                                </button>
                              )}
                              {onExportIFC && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportIFC();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  Export IFC
                                </button>
                              )}
                              {onExportDXF && (
                                <button
                                  className="action-button"
                                  style={{ flex: '1 1 auto', minWidth: '80px' }}
                                  onClick={() => {
                                    onExportDXF();
                                    handleClose();
                                  }}
                                  disabled={exportLoading}
                                >
                                  Export DXF
                                </button>
                              )}
                            </div>
                          )}

                          {/* Admin actions - render after export buttons */}
                          {adminActions.length > 0 && (
                            <>
                              {chunkActionRows(adminActions).map(
                                (rowActions, rowIndex) => (
                                  <div
                                    key={`admin-${rowIndex}`}
                                    className="action-buttons"
                                  >
                                    {rowActions.map((action) => (
                                      <button
                                        key={action.label}
                                        className="action-button admin-action-button"
                                        onClick={() =>
                                          runAction(action, handleClose)
                                        }
                                        disabled={action.disabled}
                                        title={action.title}
                                      >
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                )
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}

                    {/* Bathroom Constraints Reference - admin tool, bathrooms only */}
                    {showAdminTools &&
                      inferRoomType(roomData?.name) === 'Bathroom' && (
                        <div className="action-buttons">
                          <button
                            className="action-button admin-action-button"
                            onClick={() => {
                              setShowConstraints(true);
                              handleClose();
                            }}
                          >
                            Bathroom Constraints
                          </button>
                        </div>
                      )}

                    {/* Logout button - always last */}
                    <div className="action-buttons logout-row">
                      <button
                        className="action-button logout-action"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
        title="Logout Confirmation"
        message="Are you sure you want to logout from your account?"
        confirmText="Logout"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />

      <BathroomConstraintsModal
        isOpen={showConstraints}
        onClose={() => setShowConstraints(false)}
        roomData={roomData}
        structureData={structureData}
        disabledConstraints={disabledConstraints}
        onDisabledConstraintsChange={onDisabledConstraintsChange}
      />
    </>
  );
};

export default LogoPopup;
